/**
 * DevProtectedRoute.tsx
 * 
 * Protects routes to only allow developer access
 * Uses IP whitelist and environment variables for security
 */

import React, { useState, useEffect } from 'react'
import { Navigate } from 'react-router-dom'

interface DevProtectedRouteProps {
  children: React.ReactNode
}

const DevProtectedRoute: React.FC<DevProtectedRouteProps> = ({ children }) => {
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const checkAccess = async () => {
      try {
        // Check if we're in development mode
        const isDev = import.meta.env.DEV
        
        // Get allowed IPs from environment variables
        const allowedIPs = import.meta.env.VITE_DEV_ALLOWED_IPS?.split(',') || []
        const devPassword = import.meta.env.VITE_DEV_PASSWORD
        
        // In development, allow access
        if (isDev) {
          setIsAuthorized(true)
          setIsLoading(false)
          return
        }
        
        // In production, check IP and/or password
        if (allowedIPs.length > 0) {
          // Get user's IP
          const response = await fetch('https://api.ipify.org?format=json')
          const data = await response.json()
          const userIP = data.ip
          
          if (allowedIPs.includes(userIP)) {
            setIsAuthorized(true)
          } else {
            setIsAuthorized(false)
          }
        } else if (devPassword) {
          // Use password-based access
          const storedPassword = localStorage.getItem('dev_access_password')
          if (storedPassword === devPassword) {
            setIsAuthorized(true)
          } else {
            // Prompt for password
            const enteredPassword = prompt('Enter developer access password:')
            if (enteredPassword === devPassword) {
              localStorage.setItem('dev_access_password', devPassword)
              setIsAuthorized(true)
            } else {
              setIsAuthorized(false)
            }
          }
        } else {
          // No protection configured
          setIsAuthorized(false)
        }
      } catch (error) {
        console.error('Error checking developer access:', error)
        setIsAuthorized(false)
      } finally {
        setIsLoading(false)
      }
    }

    checkAccess()
  }, [])

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Verifying Access</h2>
          <p className="text-gray-600">Checking developer permissions...</p>
        </div>
      </div>
    )
  }

  if (!isAuthorized) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">🔒</div>
            <h1 className="text-2xl font-bold text-gray-900 mb-4">
              Access Restricted
            </h1>
            <p className="text-gray-600 mb-6">
              This area is restricted to authorized developers only.
            </p>
            <button
              onClick={() => window.location.href = '/'}
              className="bg-primary-500 text-white px-6 py-2 rounded-lg hover:bg-primary-600 transition-colors"
            >
              Go to Home
            </button>
          </div>
        </div>
      </div>
    )
  }

  return <>{children}</>
}

export default DevProtectedRoute
